// A copy of the fullstack's base-layer/app/composables/useFitText.js (2026-10-31, for the
// Unlock Masterclass page's title; keep the two the same until the Frontend is migrated).
// composables/useFitText.js
// Sizes an element's font so its text fills the parent's content width on one line.
// Proportional, not stepped: text width scales linearly with font-size, so one measurement
// gives the fitted size (a second pass corrects rounding). Re-fits when the parent resizes,
// the text changes, or web fonts finish loading.
// Why this over the alternatives (researched Sep 2026):
// - useMaxFontSize / MaxFontSize: 2px stepping leaves visible gaps at small sizes.
// - CSS-only hacks (container units + tan(atan2()) with duplicated text): fragile, extra markup.
// - fitty / vue3-fitty: same proportional idea, but an extra dependency for a few lines of code.
// - Native CSS `text-fit` / `text-grow` (css-fonts-5): best long-term, but not reliably shipped
//   yet; revisit as a progressive enhancement.

import {computed, nextTick, onBeforeUnmount, onMounted, ref} from "vue";
import {useResizeObserver, useMutationObserver} from "@vueuse/core";

function getParentContentWidth(element) {
  const parent = element.parentElement;
  if (!parent) return 0;
  const style = window.getComputedStyle(parent);
  return parent.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
}

function getFittedSize(element, availableWidth, {minSize, maxSize}) {
  const currentSize = parseFloat(window.getComputedStyle(element).fontSize);
  const textWidth = element.getBoundingClientRect().width;
  if (!currentSize || !textWidth) return currentSize;
  const size = Math.floor(((currentSize * availableWidth) / textWidth) * 10) / 10;
  return Math.min(maxSize, Math.max(minSize, size));
}

function fitText(element, options) {
  if (!element) return "";
  element.style.whiteSpace = "nowrap";
  element.style.display = "inline-block";
  const availableWidth = getParentContentWidth(element);
  if (availableWidth <= 0) return "";
  for (let pass = 0; pass < 2; pass++) {
    element.style.fontSize = `${getFittedSize(element, availableWidth, options)}px`;
  }
  return element.style.fontSize;
}

// Returns {fit, fontSize}: fontSize is the last fitted size (e.g. "58.3px"), for reuse elsewhere
export function useFitText(elementRef, {minSize = 8, maxSize = 300} = {}) {
  const fontSize = ref("");
  const fit = () => {
    const size = fitText(elementRef.value, {minSize, maxSize});
    if (size) fontSize.value = size;
  };
  const parentRef = computed(() => elementRef.value?.parentElement || null);
  useResizeObserver(parentRef, fit);
  useMutationObserver(elementRef, fit, {characterData: true, childList: true, subtree: true});
  onMounted(async () => {
    await nextTick();
    fit();
    await document.fonts?.ready;
    fit();
    document.fonts?.addEventListener?.("loadingdone", fit);
  });
  onBeforeUnmount(() => document.fonts?.removeEventListener?.("loadingdone", fit));
  return {fit, fontSize};
}
